import { useState, useEffect } from 'react';
import { get, post, patch, del } from '../helpers/apiHelper';

export const useMarketingObjectives = (userId) => {
    const [activeObjective, setActiveObjective] = useState(null);
    const [objectivesHistory, setObjectivesHistory] = useState([]);
    const [objectiveTypes, setObjectiveTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Obtener el objetivo activo
    const fetchActiveObjective = async () => {
        try {
            const objective = await get('marketing-objectives/active');
            setActiveObjective(objective);
        } catch (error) {
            console.error('Error al obtener objetivo activo:', error);
            setActiveObjective(null);
        }
    };

    // Obtener tipos de objetivos disponibles
    const fetchObjectiveTypes = async () => {
        try {
            const types = await get('marketing-objectives/types');
            setObjectiveTypes(types);
        } catch (error) {
            console.error('Error al obtener tipos de objetivos:', error);
            setObjectiveTypes([]);
        }
    };

    // Obtener historial de objetivos
    const fetchObjectivesHistory = async () => {
        try {
            const history = await get('marketing-objectives/history');
            setObjectivesHistory(history);
        } catch (error) {
            console.error('Error al obtener historial de objetivos:', error);
            setObjectivesHistory([]);
        }
    };

    // Crear nuevo objetivo
    const createObjective = async (objectiveData) => {
        try {
            const newObjective = await post('marketing-objectives', objectiveData);
            await fetchActiveObjective(); // Actualizar objetivo activo
            await fetchObjectivesHistory(); // Actualizar historial
            return newObjective;
        } catch (error) {
            console.error('Error al crear objetivo:', error);
            throw error;
        }
    };

    // Cambiar objetivo activo
    const changeActiveObjective = async (objectiveData) => {
        try {
            const changedObjective = await post('marketing-objectives/change', objectiveData);
            await fetchActiveObjective(); // Actualizar objetivo activo
            await fetchObjectivesHistory(); // Actualizar historial
            return changedObjective;
        } catch (error) {
            console.error('Error al cambiar objetivo:', error);
            throw error;
        }
    };

    // Actualizar objetivo
    const updateObjective = async (objectiveId, updateData) => {
        try {
            const updatedObjective = await patch(`marketing-objectives/${objectiveId}`, updateData);
            await fetchActiveObjective(); // Actualizar objetivo activo
            await fetchObjectivesHistory(); // Actualizar historial
            return updatedObjective;
        } catch (error) {
            console.error('Error al actualizar objetivo:', error);
            throw error;
        }
    };

    // Eliminar objetivo
    const deleteObjective = async (objectiveId) => {
        try {
            await del(`marketing-objectives/${objectiveId}`);
            await fetchActiveObjective(); // Actualizar objetivo activo
            await fetchObjectivesHistory(); // Actualizar historial
        } catch (error) {
            console.error('Error al eliminar objetivo:', error);
            throw error;
        }
    };

    // Cargar datos iniciales
    useEffect(() => {
        const loadData = async () => {
            if (userId) {
                setLoading(true);
                setError(null);

                try {
                    // Intentar cargar datos en paralelo, pero manejar errores individualmente
                    const promises = [
                        fetchActiveObjective().catch(err => {
                            console.log('No se pudo cargar objetivo activo:', err);
                            return null;
                        }),
                        fetchObjectiveTypes().catch(err => {
                            console.log('No se pudo cargar tipos de objetivos:', err);
                            return null;
                        }),
                        fetchObjectivesHistory().catch(err => {
                            console.log('No se pudo cargar historial:', err);
                            return null;
                        })
                    ];

                    await Promise.all(promises);
                } catch (error) {
                    console.error('Error general al cargar datos:', error);
                    // No establecer error general, solo log
                } finally {
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        };

        loadData();
    }, [userId]);

    return {
        activeObjective,
        objectivesHistory,
        objectiveTypes,
        loading,
        error,
        createObjective,
        changeActiveObjective,
        updateObjective,
        deleteObjective,
        refreshData: () => {
            fetchActiveObjective();
            fetchObjectivesHistory();
        }
    };
}; 